/**
 * System Quality Component
 * Displays profit factor, expectancy, Kelly %, SQN, and P&L standard deviation
 */

import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { StatisticsCardHeadline } from "./statisticsCardHeadline";
import {
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsDefinitionTooltip,
} from "./statisticsChartTooltip";
import { DataTable } from "../../../../components/ui/data-table";

interface SystemQualityTableProps {
  statistics: {
    totalPnL: number;
    totalTrades: number;
  };
  backendStats?: {
    profit_factor?: number | null;
    kelly_percentage?: number | null;
    system_quality_number?: number | null;
    trade_pnl_standard_deviation?: number | null;
  } | null;
}

interface QualityRow {
  metric: string;
  value: string;
}

const SYSTEM_QUALITY_TOOLTIPS: Record<string, string> = {
  "Profit Factor":
    "Gross profits divided by gross losses. A value > 1 means profitable overall; higher indicates stronger profitability relative to losses.",
  Expectancy:
    "Average P&L per trade. Conceptually: (Win% × Avg Win) − (Loss% × Avg Loss).",
  "Kelly Percentage":
    "A position sizing guideline based on edge and win/loss characteristics. Higher values suggest a stronger edge; use cautiously in practice.",
  SQN:
    "System Quality Number — a risk-adjusted performance score that accounts for average trade performance and variability. Higher is better.",
  "P&L Std Deviation":
    "Standard deviation of trade P&L. Higher values indicate more variability in outcomes from trade to trade.",
};

const SystemQualityTable: React.FC<SystemQualityTableProps> = ({
  statistics,
  backendStats,
}) => {
  const { formatMoney } = useDisplayCurrency();

  const expectancy = statistics.totalTrades > 0
    ? statistics.totalPnL / statistics.totalTrades
    : 0;

  const tableData: QualityRow[] = useMemo(() => [
    {
      metric: "Profit Factor",
      value: backendStats?.profit_factor != null ? backendStats.profit_factor.toFixed(2) : "N/A",
    },
    {
      metric: "Expectancy",
      value: formatMoney(expectancy),
    },
    {
      metric: "Kelly Percentage",
      value: backendStats?.kelly_percentage != null ? `${backendStats.kelly_percentage.toFixed(2)}%` : "N/A",
    },
    {
      metric: "SQN",
      value: backendStats?.system_quality_number != null ? backendStats.system_quality_number.toFixed(2) : "N/A",
    },
    {
      metric: "P&L Std Deviation",
      value: backendStats?.trade_pnl_standard_deviation != null
        ? formatMoney(backendStats.trade_pnl_standard_deviation)
        : "N/A",
    },
  ], [backendStats, expectancy, formatMoney]);

  const columns: ColumnDef<QualityRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => {
          const metric = row.getValue("metric") as string;
          const description = SYSTEM_QUALITY_TOOLTIPS[metric];

          if (description) {
            return (
              <StatisticsDefinitionTooltip label={metric} description={description} />
            );
          }

          return (
            <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
              {metric}
            </span>
          );
        },
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("value")}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-background p-4">
      <div className="mb-4">
        <StatisticsCardHeadline>System Quality</StatisticsCardHeadline>
      </div>
      <DataTable
        columns={columns}
        data={tableData}
        showPagination={false}
        emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
      />
    </div>
  );
};

export default SystemQualityTable;
